import { useEffect, useMemo, useState } from 'react';
import { diffLines } from 'diff';
import { adminApi } from '../../utils/api';
import { Alert, Badge, Button, Modal, Spinner } from './shared';

const STEP_PREVIEW = 'preview';
const STEP_RESULT = 'result';

const RESOURCE_LABEL = {
    skill: { single: 'skill', plural: 'skills', manifest: 'SKILL.md' },
    subagent: { single: 'sub-agent', plural: 'sub-agents', manifest: 'SUBAGENT.md' },
    knowledge: { single: 'knowledge file', plural: 'knowledge files', manifest: 'KNOWLEDGE.md' },
};


export default function SyncRunModal({ open, configId, resourceType, onClose, onSuccess }) {
    const [step, setStep] = useState(STEP_PREVIEW);
    const [loading, setLoading] = useState(false);
    const [previewData, setPreviewData] = useState(null);
    const [selectedFolders, setSelectedFolders] = useState(new Set());
    const [overwrite, setOverwrite] = useState(true);
    const [syncResult, setSyncResult] = useState(null);
    const [error, setError] = useState(null);

    const labels = RESOURCE_LABEL[resourceType] || RESOURCE_LABEL.skill;

    useEffect(() => {
        if (!open || !configId) return;
        setStep(STEP_PREVIEW);
        setError(null);
        setPreviewData(null);
        setSyncResult(null);
        loadPreview();
    }, [open, configId]);

    const loadPreview = async () => {
        setLoading(true);
        try {
            const result = await adminApi.previewSync(configId);
            setPreviewData(result);
            setSelectedFolders(new Set(
                (result || [])
                    .filter((s) => !s.validation_error)
                    .map((s) => s.folder)
            ));
        } catch (e) {
            setError(e?.message || String(e));
        } finally {
            setLoading(false);
        }
    };

    const toggle = (folder) => {
        setSelectedFolders((prev) => {
            const next = new Set(prev);
            if (next.has(folder)) next.delete(folder); else next.add(folder);
            return next;
        });
    };

    const toggleAll = () => {
        const valid = (previewData || []).filter((s) => !s.validation_error).map((s) => s.folder);
        setSelectedFolders(selectedFolders.size === valid.length ? new Set() : new Set(valid));
    };

    const handleSync = async () => {
        if (selectedFolders.size === 0) {
            setError(`Select at least one ${labels.single} to sync`);
            return;
        }
        setLoading(true);
        setError(null);
        try {
            const result = await adminApi.triggerSync(configId, {
                selected_ids: Array.from(selectedFolders),
                overwrite,
            });
            setSyncResult(result);
            setStep(STEP_RESULT);
            const n = result.synced?.length || 0;
            if (n > 0) onSuccess?.(`Synced ${n} ${n === 1 ? labels.single : labels.plural}`);
        } catch (e) {
            setError(e?.message || String(e));
        } finally {
            setLoading(false);
        }
    };

    const title = step === STEP_RESULT ? 'Sync complete' : `Preview ${labels.plural} — ${configId}`;
    const total = previewData?.length || 0;

    return (
        <Modal open={open} onClose={() => !loading && onClose()} title={title} size="lg">
            <Modal.Body>
                {loading && !previewData ? (
                    <Spinner size="md" text="Loading preview..." center />
                ) : step === STEP_PREVIEW ? (
                    <PreviewStep
                        previewData={previewData}
                        selectedFolders={selectedFolders}
                        labels={labels}
                        error={error}
                        overwrite={overwrite}
                        onToggleOverwrite={setOverwrite}
                        onToggle={toggle}
                        onToggleAll={toggleAll}
                    />
                ) : (
                    <ResultStep syncResult={syncResult} />
                )}
            </Modal.Body>
            <Modal.Footer>
                {step === STEP_PREVIEW ? (
                    <>
                        <Button variant="ghost" onClick={onClose} disabled={loading}>Cancel</Button>
                        <Button onClick={handleSync} loading={loading} disabled={selectedFolders.size === 0 || total === 0}>
                            Sync {selectedFolders.size > 0 ? `${selectedFolders.size} ${selectedFolders.size === 1 ? labels.single : labels.plural}` : ''}
                        </Button>
                    </>
                ) : (
                    <Button onClick={onClose}>Done</Button>
                )}
            </Modal.Footer>
        </Modal>
    );
}

function PreviewStep({ previewData, selectedFolders, labels, error, overwrite, onToggleOverwrite, onToggle, onToggleAll }) {
    const items = previewData || [];
    const valid = items.filter((s) => !s.validation_error);
    const allSelected = valid.length > 0 && selectedFolders.size === valid.length;
    const existingSelected = valid.filter(
        (s) => s.exists && selectedFolders.has(s.folder)
    );

    return (
        <div className="space-y-3">
            <div className="flex items-center justify-between">
                <div className="text-sm text-gray-600">
                    Found <span className="font-medium text-gray-900">{items.length}</span> folder{items.length !== 1 ? 's' : ''} containing <code className="text-xs bg-gray-100 px-1 rounded">{labels.manifest}</code>
                </div>
                {valid.length > 0 && (
                    <button className="text-xs text-blue-600 hover:underline" onClick={onToggleAll}>
                        {allSelected ? 'Deselect all' : 'Select all'}
                    </button>
                )}
            </div>

            <label className="flex items-center gap-2 text-xs text-gray-600">
                <input type="checkbox" className="rounded border-gray-300 text-blue-600" checked={overwrite} onChange={(e) => onToggleOverwrite(e.target.checked)} />
                <span>Overwrite existing {labels.plural} with the same name</span>
            </label>

            {existingSelected.length > 0 && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 flex items-center gap-2">
                    <svg className="w-4 h-4 text-amber-600 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                    </svg>
                    <span className="text-xs text-amber-800">
                        {existingSelected.length} selected {labels.plural} will be overridden
                    </span>
                </div>
            )}

            <div className="border border-gray-200 rounded-lg divide-y divide-gray-100 max-h-96 overflow-y-auto">
                {items.length === 0 ? (
                    <div className="px-4 py-6 text-center text-sm text-gray-400">
                        No folders found. Check that the scan paths contain subdirectories with <code className="bg-gray-100 px-1 rounded">{labels.manifest}</code>.
                    </div>
                ) : items.map((spec) => (
                    <ResourcePreviewRow
                        key={spec.folder}
                        spec={spec}
                        labels={labels}
                        checked={selectedFolders.has(spec.folder)}
                        onToggle={() => !spec.validation_error && onToggle(spec.folder)}
                    />
                ))}
            </div>

            {error && <Alert variant="danger">{error}</Alert>}
        </div>
    );
}

function ResourcePreviewRow({ spec, labels, checked, onToggle }) {
    const [expanded, setExpanded] = useState(false);
    const [diffOpen, setDiffOpen] = useState(false);

    const hasConflict = spec.exists && spec.existing;
    const hasDiff = hasConflict
        && spec.existing_content != null
        && spec.incoming_content != null
        && spec.existing_content !== spec.incoming_content;

    return (
        <>
            <DiffModal
                open={diffOpen}
                onClose={() => setDiffOpen(false)}
                title={`${labels.manifest} diff — ${spec.resource_id || spec.folder}`}
                oldContent={spec.existing_content}
                newContent={spec.incoming_content}
            />
            <div className={`px-3 py-2.5 ${spec.validation_error ? 'bg-red-50/50' : ''}`}>
                <div className="flex items-start gap-2.5">
                    <input
                        type="checkbox"
                        className="mt-0.5 rounded border-gray-300 text-blue-600 disabled:opacity-40"
                        checked={checked}
                        onChange={onToggle}
                        disabled={!!spec.validation_error}
                    />
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5 flex-wrap">
                            <span className="text-sm font-medium text-gray-900 truncate">{spec.resource_name || spec.folder}</span>
                            {spec.resource_id && spec.resource_id !== (spec.resource_name || spec.folder) && (
                                <Badge variant="outline" className="text-xs shrink-0">{spec.resource_id}</Badge>
                            )}
                            {!spec.exists && !spec.validation_error && (
                                <Badge variant="success" className="text-xs shrink-0">New</Badge>
                            )}
                            {spec.exists && !hasDiff && (
                                <Badge variant="outline" className="text-xs shrink-0">Exists (no change)</Badge>
                            )}
                            {spec.exists && hasDiff && (
                                <Badge variant="warning" className="text-xs shrink-0">Exists (modified)</Badge>
                            )}
                            {spec.validation_error && (
                                <Badge variant="danger" className="text-xs shrink-0">Invalid</Badge>
                            )}
                        </div>
                        {spec.description && <p className="text-xs text-gray-400 mt-0.5 truncate">{spec.description}</p>}
                        {spec.validation_error && <p className="text-xs text-red-500 mt-0.5">{spec.validation_error}</p>}
                        <div className="flex items-center gap-3 mt-1">
                            <span className="text-xs text-gray-400">
                                {(spec.files || []).length} file{(spec.files || []).length !== 1 ? 's' : ''}
                            </span>
                            {spec.metadata?.source_path && (
                                <span className="text-xs text-gray-400 font-mono truncate">{spec.metadata.source_path}</span>
                            )}
                            {hasConflict && (
                                <button className="text-xs text-amber-600 hover:underline" onClick={() => setExpanded(v => !v)}>
                                    {expanded ? 'Hide' : 'Compare versions'}
                                </button>
                            )}
                            {hasDiff && (
                                <button className="text-xs text-blue-600 hover:underline" onClick={() => setDiffOpen(true)}>
                                    Xem thay đổi
                                </button>
                            )}
                        </div>
                        {expanded && hasConflict && (
                            <div className="grid grid-cols-2 gap-2 mt-2">
                                <div className="rounded border border-gray-200 bg-white p-2">
                                    <div className="text-xs font-medium text-gray-500 mb-1">Current</div>
                                    <div className="text-xs text-gray-700">{spec.existing.name}</div>
                                    {spec.existing.description && (
                                        <div className="text-xs text-gray-400 mt-0.5 line-clamp-2">{spec.existing.description}</div>
                                    )}
                                    <div className="text-xs text-gray-400 mt-1">
                                        {spec.existing.resource_count || 0} resources
                                    </div>
                                </div>
                                <div className="rounded border border-blue-200 bg-blue-50 p-2">
                                    <div className="text-xs font-medium text-blue-600 mb-1">Incoming</div>
                                    <div className="text-xs text-gray-700">{spec.resource_name || spec.folder}</div>
                                    {spec.description && (
                                        <div className="text-xs text-gray-400 mt-0.5 line-clamp-2">{spec.description}</div>
                                    )}
                                    <div className="text-xs text-gray-400 mt-1">{(spec.files || []).length} files</div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
}

function DiffModal({ open, onClose, title, oldContent, newContent }) {
    const changes = useMemo(() => {
        if (!open) return null;
        if (oldContent == null || newContent == null) return null;
        if (oldContent === newContent) return null;
        return diffLines(oldContent, newContent);
    }, [open, oldContent, newContent]);

    if (!open) return null;

    return (
        <div className="fixed inset-0 z-[60] flex items-center justify-center px-4 py-8">
            <div className="absolute inset-0 bg-black/50" onClick={onClose} />
            <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-3xl flex flex-col max-h-[calc(100vh-4rem)]">
                <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 shrink-0">
                    <div>
                        <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
                        <div className="flex items-center gap-3 mt-0.5 text-xs text-gray-400">
                            <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-red-100 border border-red-300 inline-block" />removed</span>
                            <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-green-100 border border-green-300 inline-block" />added</span>
                        </div>
                    </div>
                    <button
                        className="p-1 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100"
                        onClick={onClose}
                    >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>
                <div className="overflow-auto flex-1">
                    {!changes ? (
                        <p className="text-sm text-gray-400 text-center py-8">Content is identical — no changes.</p>
                    ) : (
                        <pre className="text-xs leading-5 font-mono">
                            {changes.map((part, i) => {
                                const lines = part.value.split('\n').filter((_, idx, arr) => idx < arr.length - 1 || part.value.endsWith('\n') || arr[idx] !== '');
                                const prefix = part.added ? '+' : part.removed ? '-' : ' ';
                                const cls = part.added
                                    ? 'text-green-800 bg-green-50'
                                    : part.removed
                                        ? 'text-red-800 bg-red-50'
                                        : 'text-gray-500';
                                return lines.map((line, j) => (
                                    <div key={`${i}-${j}`} className={`px-4 whitespace-pre ${cls}`}>
                                        {prefix}{line}
                                    </div>
                                ));
                            })}
                        </pre>
                    )}
                </div>
                <div className="px-4 py-3 border-t border-gray-200 flex justify-end shrink-0">
                    <button
                        className="px-3 py-1.5 text-sm rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700"
                        onClick={onClose}
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
}

function ResultStep({ syncResult }) {
    if (!syncResult) return null;
    const { synced = [], skipped = [], errors = [] } = syncResult;
    return (
        <div className="space-y-3">
            {synced.length > 0 && (
                <div className="rounded-lg border border-green-200 bg-green-50 px-3 py-2.5">
                    <div className="text-xs font-medium text-green-800 mb-1.5">Synced ({synced.length})</div>
                    <div className="flex flex-wrap gap-1">{synced.map((id) => <Badge key={id} variant="success" className="text-xs">{id}</Badge>)}</div>
                </div>
            )}
            {skipped.length > 0 && (
                <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5">
                    <div className="text-xs font-medium text-gray-600 mb-1.5">Skipped ({skipped.length})</div>
                    <div className="flex flex-wrap gap-1">{skipped.map((id) => <Badge key={id} variant="outline" className="text-xs">{id}</Badge>)}</div>
                </div>
            )}
            {errors.length > 0 && (
                <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2.5">
                    <div className="text-xs font-medium text-red-800 mb-1.5">Errors ({errors.length})</div>
                    <div className="space-y-1">
                        {errors.map((e, i) => <div key={i} className="text-xs text-red-700"><span className="font-medium">{e.id || e.skill}</span>: {e.error}</div>)}
                    </div>
                </div>
            )}
            {synced.length === 0 && skipped.length === 0 && errors.length === 0 && (
                <p className="text-sm text-gray-500 text-center py-4">Nothing to sync.</p>
            )}
        </div>
    );
}
